import React from 'react';

/**
 * AppErrorBoundary - boot resilience.
 *
 * On 2026-11-04 a mount-time throw inside the studio (a bad
 * property read in the pinch-zoom wiring) propagated to the root
 * and unmounted the ENTIRE app: every visitor got an empty dark
 * page. No single component may ever black out the product like
 * that again. This boundary catches a render/effect crash in its
 * subtree and replaces ONLY that subtree with a recoverable
 * fallback: the surrounding app (sidebar, navigation, other
 * routes) stays alive, and the user gets honest actions - retry
 * the subtree in place, or reload the page. Saved designs live in
 * localStorage / the account API, not in the crashed subtree's
 * memory, and the fallback says so.
 */
interface Props {
    children: React.ReactNode;
    /** What the boundary guards, for the fallback copy ("the studio"). */
    label?: string;
}

interface State {
    error: Error | null;
}

export class AppErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Keep the failure visible in the console for diagnosis;
        // never swallow it silently.
        console.error(`[AppErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info);
    }

    private retry = () => this.setState({ error: null });

    render() {
        if (!this.state.error) return this.props.children;
        const label = this.props.label || 'this part of the app';
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '320px', padding: '32px 16px', boxSizing: 'border-box'
            }}>
                <div style={{
                    maxWidth: '440px', width: '100%', textAlign: 'center',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '16px', padding: '28px 24px', color: '#e5e7eb',
                    fontFamily: 'inherit'
                }}>
                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>⚠️</div>
                    <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#fff' }}>
                        Something went wrong in {label}
                    </h2>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', lineHeight: 1.55, color: '#c7ccd6' }}>
                        It hit a problem and stopped before it could finish loading.
                        Your saved designs are stored separately and are not affected.
                    </p>
                    <p style={{
                        margin: '0 0 18px', fontSize: '11px', lineHeight: 1.5, color: '#8b93a5',
                        wordBreak: 'break-word'
                    }}>
                        {String((this.state.error && this.state.error.message) || this.state.error)}
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={this.retry}
                            style={{
                                padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                                background: '#f97316', color: '#fff', fontWeight: 600, fontSize: '13px'
                            }}
                        >
                            Try again
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '10px 18px', borderRadius: '10px', cursor: 'pointer',
                                background: 'transparent', color: '#e5e7eb', fontWeight: 600, fontSize: '13px',
                                border: '1px solid rgba(255,255,255,0.25)'
                            }}
                        >
                            Reload the page
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default AppErrorBoundary;
